import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords: string[];
  canonicalPath?: string;
  imageUrl?: string;
}

const SEO: React.FC<SEOProps> = ({ 
  title, 
  description, 
  keywords,
  canonicalPath = '',
  imageUrl = '/social-preview.jpg'
}) => {
  const siteTitle = 'Kalculas';
  const domain = 'kalculas.com';
  const fullTitle = `${title} | ${siteTitle}`;
  const canonicalUrl = `https://${domain}${canonicalPath}`;
  const fullImageUrl = `https://${domain}${imageUrl}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />
      
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={`${title} calculator preview`} />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@kalculas" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#14b8a6" />
      
      {/* Additional meta tags for better integration detection */}
      <meta name="generator" content="Kalculas Financial Calculator Suite" />
      <meta name="application-name" content="Kalculas" />
      <meta name="msapplication-TileColor" content="#14b8a6" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Open Graph additional tags */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter additional tags */}
      <meta name="twitter:creator" content="@kalculas" />
      <meta name="twitter:image:alt" content={`${title} - Financial Calculator`} />
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": fullTitle,
          "description": description,
          "url": canonicalUrl,
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Any",
          "browserRequirements": "Requires JavaScript. Requires HTML5.",
          "softwareVersion": "1.0.0",
          "keywords": keywords.join(', '),
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "provider": {
            "@type": "Organization",
            "name": siteTitle,
            "url": `https://${domain}`
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;